import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { type EvidenceOut, type FieldResultOut } from "@platform/api/golden";
import { Chip, Typography } from "@platform/design-system";

import { EvidenceList } from "../shared/EvidenceList";
import { formatValue } from "../shared/values";

import { askedForHelp } from "./verdicts";

import type { ReactNode } from "react";

// An unexpected entity is one RUMI produced that the analyst's submission has no
// counterpart for, so its expected and analyst values are not blank but absent.
const NotInSubmission = (): ReactNode => (
  <Typography variant="body-medium" color="textSecondary" component="span">
    not in the analyst&apos;s submission
  </Typography>
);

const Flag = ({ label }: { readonly label: string }): ReactNode => (
  <Chip
    size="small"
    variant="subtle"
    color="attention"
    leadingVisual={false}
    label={label}
  />
);

/** One discrepancy as a reviewer weighs it: its values side by side, the judge's call, and RUMI's evidence. */
export const DiscrepancyFacts = ({
  caseId,
  result,
  evidence,
  onEvidenceError,
}: {
  readonly caseId: number;
  readonly result: FieldResultOut;
  readonly evidence: readonly EvidenceOut[];
  readonly onEvidenceError: (message: string) => void;
}): ReactNode => {
  const isUnexpected = result.outcome === "unexpected";
  const judgeDifferences = result.judge_differences ?? [];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box
        sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}
      >
        <Box component="span" sx={{ fontFamily: "monospace" }}>
          {result.field_path}
        </Box>
        <Chip
          size="small"
          variant="subtle"
          leadingVisual={false}
          label={result.outcome}
        />
        {askedForHelp(evidence) && <Flag label="RUMI asked for help" />}
        {result.ambiguous && <Flag label="ambiguous pairing" />}
        {result.missing_entity && (
          <Box component="span" sx={{ fontFamily: "monospace" }}>
            {result.missing_entity}
          </Box>
        )}
      </Box>
      <Table size="small" aria-label="Values">
        <TableHead>
          <TableRow>
            <TableCell>Expected</TableCell>
            <TableCell>RUMI</TableCell>
            <TableCell>Analyst sent</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell>
              {isUnexpected ? (
                <NotInSubmission />
              ) : (
                formatValue(result.expected_value)
              )}
            </TableCell>
            <TableCell>{formatValue(result.rumi_value)}</TableCell>
            <TableCell>
              {isUnexpected ? (
                <NotInSubmission />
              ) : (
                formatValue(result.baseline_value)
              )}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
      {result.judge_verdict && (
        <Typography variant="body-medium" color="textSecondary">
          Judge: {result.judge_verdict}
          {judgeDifferences.length > 0 && ` — ${judgeDifferences.join("; ")}`}
        </Typography>
      )}
      <Typography variant="subtitle2" component="h4">
        RUMI&apos;s evidence
      </Typography>
      <EvidenceList
        caseId={caseId}
        evidence={evidence}
        onError={onEvidenceError}
      />
    </Box>
  );
};
